import React from "react";
import { Link, useParams } from "react-router-dom";
import Header from "components/ui/Header";
import Footer from "components/Footer";
import SEO from "components/SEO";
import NotFound from "pages/NotFound";
import { developerProfiles } from "data/profilesData";

const DeveloperProfilePage = () => {
  const { developerSlug } = useParams();
  const developer = developerProfiles.find((entry) => entry.slug === developerSlug);

  if (!developer) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-[#f4faf8]">
      <SEO
        title={`${developer.name} | Developer profile for hire | OctalChip`}
        description={developer.summary}
        keywords={`${developer.name}, hire developer, software engineer for hire, staffing, OctalChip`}
        canonical={`https://www.octalchip.com/developers/${developer.slug}`}
      />
      <Header />
      <main className="py-10 sm:py-14">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 max-w-4xl">
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10 mb-8">
            <p className="text-sm font-semibold tracking-wide text-[#00d690] uppercase mb-1">OctalChip</p>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Developer profile for client staffing review</p>
            <div className="mb-4 h-0.5 w-14 rounded-full bg-[#00d690]" aria-hidden />
            <h1 className="text-3xl sm:text-[2.125rem] sm:leading-tight font-bold text-gray-900 mb-2">{developer.name}</h1>
            <p className="text-[#00d690] font-medium text-lg mb-4">{developer.headline}</p>
            <p className="text-gray-700 leading-relaxed">{developer.summary}</p>
            <p className="mt-5 text-sm text-gray-500 border border-gray-100 bg-gray-50 rounded-xl px-4 py-3">
              Engagements are contracted through OctalChip. Availability, commercials, overlap hours, and any technical checkpoint you require are coordinated with our team once you indicate interest.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="https://cal.com/octalchip/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-5 py-2.5 rounded-full bg-[#00d690] text-white font-medium hover:bg-[#00b876] transition-colors text-sm"
              >
                Discuss hiring this profile
              </a>
              <Link
                to="/contact"
                className="inline-block px-5 py-2.5 rounded-full border border-[#00d690] text-[#00d690] font-medium hover:bg-[#00d690] hover:text-white transition-colors text-sm"
              >
                Request screening or intro
              </Link>
            </div>
          </section>

          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6">
            <div className="mb-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">Snapshot for your evaluation</h2>
              <div className="mt-2 h-0.5 w-14 rounded-full bg-[#00d690]" aria-hidden />
            </div>
            <ul className="space-y-2 list-disc pl-5">
              {developer.highlights.map((highlight) => (
                <li key={highlight} className="text-gray-700">{highlight}</li>
              ))}
            </ul>
          </section>

          <section className="grid md:grid-cols-2 gap-6 mb-6">
            <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              <div className="mb-4">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">What you can assign</h2>
                <div className="mt-2 h-0.5 w-14 rounded-full bg-[#00d690]" aria-hidden />
              </div>
              <ul className="space-y-2 list-disc pl-5">
                {developer.capabilities.map((item) => (
                  <li key={item} className="text-gray-700">{item}</li>
                ))}
              </ul>
            </article>
            <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              <div className="mb-4">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">
                  When this hire is a strong match
                </h2>
                <div className="mt-2 h-0.5 w-14 rounded-full bg-[#00d690]" aria-hidden />
              </div>
              <ul className="space-y-2 list-disc pl-5">
                {developer.engagementFit.map((item) => (
                  <li key={item} className="text-gray-700">{item}</li>
                ))}
              </ul>
            </article>
          </section>

          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <div className="mb-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">
                Next steps with OctalChip
              </h2>
              <div className="mt-2 h-0.5 w-14 rounded-full bg-[#00d690]" aria-hidden />
            </div>
            <p className="text-gray-700 mb-4">
              Share the role, stack, timeline, and collaboration model you need. We confirm fit, capacity, and how this person joins your team (dedicated, project-based, or flexible hours), including any interview or technical step you require.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://cal.com/octalchip/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-5 py-2.5 rounded-full bg-[#00d690] text-white font-medium hover:bg-[#00b876] transition-colors text-sm"
              >
                Book a staffing conversation
              </a>
              <Link
                to="/developers"
                className="inline-block px-5 py-2.5 rounded-full border border-[#00d690] text-[#00d690] font-medium hover:bg-[#00d690] hover:text-white transition-colors text-sm"
              >
                Compare other profiles
              </Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DeveloperProfilePage;
